import { Component, OnInit } from '@angular/core';
import { SelectedEventArgs } from '@syncfusion/ej2-angular-inputs';
import { EmitType } from '@syncfusion/ej2-base';
import { GridComponent, QueryCellInfoEventArgs } from '@syncfusion/ej2-angular-grids';
import { TooltipComponent } from '@syncfusion/ej2-angular-popups';
import { Tooltip } from '@syncfusion/ej2-popups';
import { ToastrService } from 'ngx-toastr';
import { IApiResult } from '../../../../shared/models/api-result';
import { ICosmeticValidationMessage } from '../../../../shared/models/cosmetic-validation-message.model';
import { AuthenticationService, SettingsService } from '../../../../shared/services';
import { fadeInOut } from '../../../../shared/helpers/animations';

@Component({
    selector: 'app-cosmetic-builder',
    templateUrl: './cosmetic-builder.component.html',
    styleUrls: ['./cosmetic-builder.component.scss'],
    animations: [fadeInOut],
    standalone: false
})
export class CosmeticBuilderComponent implements OnInit {
  loading = false;
  uploadStatus = '';
  validationStatus = '';
  validationFileName = '';
  validationStatusClassName = '';
  hasValidationErrors = false;
  downloadSPLButtonEnabled = false;
  downloadSummaryReportEnabled = false;
  isHideErrorsWarnings = false;
  tooltipObj: TooltipComponent;
  errorGrid: GridComponent;
  validationMessages: ICosmeticValidationMessage [] = [];
  ndcProductsIncludedCount: number;
  marketingEndDateRemovedProductsCount: number;
  duplicatesProductsRemovedCount: number;
  expiredProductsRemovedCount: number;
  monographMarketingCategoriesRemovedCount: number;
  summaryReportCsv: string;
  cosmeticXml: string;
  cosmeticXmlFileName: string;
  cosmetic: any;
  actionButtonText = 'Your file was built successfully. Please download the file below. This file will not be saved. Uploading a new template or refreshing the page will result in lost progress.';
  uploadSaveUrl: string;
  pragmaticResponseFile: any;
  pragmaticValidationResult: any;
  pragmaticValidationClass: string;
  pragmaticResultsFileName: string;

  constructor(private settingsService: SettingsService, private authService: AuthenticationService, private toastr: ToastrService) {
  }

  ngOnInit(): void {
    this.uploadSaveUrl = this.getProcessFormURL();
    this.initTabIndex();
  }

  initTabIndex() {
    (document.activeElement as HTMLElement).blur();

    const main = document.getElementById("firstDiv");

    if (main) {
      main.focus();
    }
  }

  public onFileUploadStart: EmitType<SelectedEventArgs> = (args: any) => {
    this.resetPage();
    if (args) {
      const authData = this.authService.getAuthData();

      args.currentRequest.setRequestHeader('authorization', this.authService.getBearerToken());
      args.currentRequest.setRequestHeader('customerGuid', this.settingsService.getCustomerGuid());
      args.currentRequest.setRequestHeader('applicationVariantId', authData.applicationVariantId ? authData.applicationVariantId.toString() : '');
      args.currentRequest.setRequestHeader('modifiedByUser', this.settingsService.getUserDataName());

      if (!args.cancel) {
        this.loading = true;
      }
    }
  }

  public onFileUploadedSuccess: EmitType<Object> = (args: any) => {
    this.loading = false;
    this.uploadStatus = 'Uploaded Successfully';

    const apiResult = JSON.parse(args.e.currentTarget.response) as IApiResult;
    const buildResult = apiResult.data;
    this.downloadSPLButtonEnabled = !this.hasValidationErrors;
    this.validationFileName = args.file.name;

    this.ndcProductsIncludedCount = apiResult.data.ndcProductsIncludedCount;
    this.marketingEndDateRemovedProductsCount = apiResult.data.marketingEndDateRemovedProductsCount;
    this.duplicatesProductsRemovedCount = apiResult.data.duplicatesProductsRemovedCount;
    this.expiredProductsRemovedCount = apiResult.data.expiredProductsRemovedCount;
    this.monographMarketingCategoriesRemovedCount = apiResult.data.monographMarketingCategoriesRemovedCount;

    this.cosmetic = buildResult.data;
    this.cosmeticXml = buildResult.xml;
    this.cosmeticXmlFileName = buildResult.xmlFileName;
    this.pragmaticResponseFile = this.cosmetic.pragmaticResponseFile;
    this.pragmaticResultsFileName = this.cosmeticXmlFileName.replace(/\.xml$/, '.html');
    this.pragmaticValidationResult = getpragmaticValidationText(this.cosmetic.pragmaticValidationResult).status;
    this.pragmaticValidationClass = getpragmaticValidationText(this.cosmetic.pragmaticValidationResult).class;
  }

  public onUploadFailure(args: any): void {
    this.loading = false;
  }

  private setDownloadClickEvent(url: string, fileName: string): void {
    const a = document.createElement('a');

    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', fileName);

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  private createBlobURL(data: string, mimeType: string): string {
    const blob = new Blob([data], {type: mimeType});
    return window.URL.createObjectURL(blob);
  }

  public downloadSummaryReport() {
    if (!this.summaryReportCsv) {
      return;
    }

    const blobUrl = this.createBlobURL(this.summaryReportCsv, 'text/csv');

    const today = new Date();

    const companyName = this.cosmetic.legalAuthenticatorCompanyName;
    const fullDate = `${today.getMonth() + 1}-${today.getDate()}-${today.getFullYear()}`;
    const fileName = `Cosmetic_Summary_Report_${companyName}_${fullDate}.csv`;

    this.setDownloadClickEvent(blobUrl, fileName);
  }

  public downloadCosmeticXml() {
    if (!this.cosmeticXml || !this.cosmeticXmlFileName) {
      return;
    }

    const blobUrl = this.createBlobURL(this.cosmeticXml, 'text/xml');

    this.setDownloadClickEvent(blobUrl, this.cosmeticXmlFileName);
  }

  public downloadPragmaticResponseFile() {
    if (!this.pragmaticResponseFile || !this.pragmaticValidationResult) {
      return;
    }

    // Decode the base64 string to a regular string
    const text = window.atob(this.pragmaticResponseFile);

    // Create a new Blob object using the decoded string and the type of the file
    const blob = new Blob([text], { type: 'text/html' });

    // Create a blob URL for the blob object
    const blobUrl = URL.createObjectURL(blob);

    this.setDownloadClickEvent(blobUrl, this.pragmaticResultsFileName);
  }

  private resetPage() {
    this.validationStatus = '';
    this.validationFileName = '';
    this.uploadStatus = '';
    this.downloadSPLButtonEnabled = false;
    this.downloadSummaryReportEnabled = false;
    this.hasValidationErrors = false;
    this.cosmeticXml = null;
    this.pragmaticResponseFile = null;
    this.pragmaticValidationResult = null;  
  }

  getProcessFormURL(): string {
    const settings = this.settingsService.getSettings();
    const apiUrl = settings.ApiGatewayUrl;
    const action = `ssdpds/v1/CosmeticBuilder/ProcessForm`;
    return `${apiUrl}/${action}`;
  }

  tooltipValue(args) {
    let grid;

    if (args.target.scrollWidth <= args.target.clientWidth) {
      args.cancel = true;
    } else {

      grid = this.errorGrid;

      const rowInfo = grid.getRowInfo(args.target);
      const rowData = rowInfo.rowData;
      this.tooltipObj.content = rowData[(rowInfo.column as any).field];
    }
  }

  customiseCell(args: QueryCellInfoEventArgs) {
    if (args.data['messageType'] === 'Error') {
      args.cell.classList.add('customcssError');
    } else if (args.data['messageType'] === 'Warning') {
      args.cell.classList.add('customcssWarning');
    }

    if (args.column.field == 'fieldName') {
      const tooltip: Tooltip = new Tooltip({
        content: args.data['fieldName'].toString()
      }, args.cell as HTMLTableCellElement);
    }
  }

  showHideErrorsWarnings() {
    this.isHideErrorsWarnings = !this.isHideErrorsWarnings;
  }
}

function getpragmaticValidationText(status) {
  let returnObj = { status: "", class: "" };
   switch (status) {
     case 1: returnObj = { status: "Pass", class:"validation-pass"};
       break;
     case 2: returnObj = { status: "Fail", class: "validation-fail"};
       break;
     case 3: returnObj = { status: "TimeOut - Please refresh the page and start the upload process again.", class: ""};
       break;
  }
  return returnObj;
}
